import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CodeBracketIcon,
  CpuChipIcon,
  GlobeAltIcon,
  UsersIcon,
  CheckCircleIcon,
  PlayIcon,
  ArrowRightIcon,
  BanknotesIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Navbar, Footer, CourseCard, Button } from './ui';
import apiService from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const LandingPage = () => {
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState([
    { label: "Students Trained", value: "0", icon: UsersIcon },
    { label: "Course Completion Rate", value: "0%", icon: TrophyIcon },
    { label: "Job Placement Rate", value: "0%", icon: CheckCircleIcon },
    { label: "Courses Available", value: "0", icon: BanknotesIcon }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForumModal, setShowForumModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  // Load courses and stats from API
  useEffect(() => {
    const loadLandingPageData = async () => {
      // Define placeholder testimonials (accessible in both try and catch blocks)
      const placeholderTestimonials = [
        {
          name: "Tordue David Antiev",
          course: "Data Analysis/Data Science",
          review: "Aitech transformed my career completely. The hands-on approach and expert mentorship helped me land my dream job in tech.",
          rating: 5,
          avatar: "T"
        },
        {
          name: "Joan Afolaranmi", 
          course: "Ethical Hacking",
          review: "The cybersecurity program at Aitech is world-class. I gained practical skills that I use every day in my security role.",
          rating: 5,
          avatar: "J"
        },
        {
          name: "Aisha Bashi",
          course: "Product Design",
          review: "From zero to professional designer in 12 weeks! The UI/UX program exceeded all my expectations.",
          rating: 5,
          avatar: "A"
        }
      ];

      try {
        setLoading(true);
        setError(null);

        // Load courses from API (now supports public access)
        const coursesData = await apiService.getCourses();
        
        // Transform course data to match landing page format
        const transformedCourses = coursesData.map((course, index) => {
          // Map course categories to icons and colors
          const courseIcons = [
            { icon: ChartBarIcon, color: "from-blue-500 to-purple-600" },
            { icon: CodeBracketIcon, color: "from-green-500 to-teal-600" },
            { icon: ShieldCheckIcon, color: "from-red-500 to-orange-600" },
            { icon: CpuChipIcon, color: "from-purple-500 to-pink-600" },
            { icon: GlobeAltIcon, color: "from-orange-500 to-red-600" },
            { icon: AcademicCapIcon, color: "from-indigo-500 to-blue-600" }
          ];
          
          const iconConfig = courseIcons[index % courseIcons.length];
          
          return {
            title: course.name,
            description: course.description,
            icon: iconConfig.icon,
            duration: `${course.duration_months} months`,
            level: "Beginner to Advanced", // Placeholder - could be added to backend model
            students: `${Math.floor(Math.random() * 3000) + 500}+`, // Placeholder
            color: iconConfig.color,
            features: ["Hands-on Projects", "Expert Mentorship", "Career Support", "Industry Recognition"],
            // Pass cost fields for price display
            cost_amount: course.cost_amount,
            cost_currency: course.cost_currency
          };
        });

        // Show only first 4 courses on landing page
        setCourses(transformedCourses.slice(0, 4));

        // Update stats based on real data
        const updatedStats = [
          { label: "Students Trained", value: "12,000+", icon: UsersIcon }, // Would come from backend
          { label: "Course Completion Rate", value: "94%", icon: TrophyIcon }, // Would come from backend
          { label: "Job Placement Rate", value: "87%", icon: CheckCircleIcon }, // Would come from backend
          { label: "Courses Available", value: `${coursesData.length}+`, icon: BanknotesIcon }
        ];

        setStats(updatedStats);
        setTestimonials(placeholderTestimonials);

      } catch (err) {
        console.error('Failed to load landing page data:', err);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to load course information. ';
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage += 'Please make sure the backend server is running on http://127.0.0.1:8000';
        } else if (err.message.includes('404')) {
          errorMessage += 'Course API endpoints may not be available yet.';
        } else {
          errorMessage += 'Please try again later.';
        }
        
        setError(errorMessage);
        
        // Set fallback data in case of error
        const fallbackCourses = [
          {
            title: "Data Science & Analytics",
            description: "Master data analysis, visualization, and machine learning with Python, R, and advanced analytics tools",
            icon: ChartBarIcon,
            duration: "12 months",
            level: "Beginner to Advanced",
            students: "2,500+",
            color: "from-blue-500 to-purple-600",
            features: ["Python & R Programming", "Machine Learning", "Data Visualization", "Statistical Analysis"]
          },
          {
            title: "Software Engineering",
            description: "Full-stack development with modern frameworks, databases, and deployment strategies",
            icon: CodeBracketIcon,
            duration: "16 months",
            level: "Beginner to Advanced", 
            students: "3,200+",
            color: "from-green-500 to-teal-600",
            features: ["React & Node.js", "Database Design", "API Development", "DevOps Practices"]
          }
        ];
        
        setCourses(fallbackCourses);
        setTestimonials(placeholderTestimonials);
      } finally {
        setLoading(false);
      }
    };

    loadLandingPageData();
  }, []);

  const navLinks = [
    { label: 'Courses', to: '#courses', onClick: null },
    { label: 'Forum', to: '#forum', onClick: () => setShowForumModal(true) },
    { label: 'Contact', to: '#contact', onClick: () => setShowContactModal(true) }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="large" message="Loading course information..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar links={navLinks} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="flex items-center mb-6">
                <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
                  🇳🇬 Nigeria's #1 Tech Training Institute
                </span>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Master <span className="text-primary-600">Tech Skills</span><br />
                Build Your <span className="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">Dream Career</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Join over 12,000 students who've transformed their careers with our AI-powered, 
                hands-on tech training programs. From Data Science to Cybersecurity - we've got you covered.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button to="/register" size="xl" className="flex items-center justify-center">
                  <PlayIcon className="w-5 h-5 mr-2" />
                  Get Started Free
                </Button>
                <Button to="/login" variant="secondary" size="xl" className="flex items-center justify-center">
                  Sign In
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <stat.icon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 mb-8">
                <div className="w-full h-80 bg-gradient-to-br from-primary-100 to-purple-100 rounded-2xl shadow-2xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🎓</div>
                    <p className="text-gray-600 font-medium">Aitech Students Learning</p>
                  </div>
                </div>
              </div>
              
              {/* Floating cards - positioned to avoid overlap */}
              <div className="absolute top-6 -left-6 bg-white rounded-xl p-3 shadow-lg animate-bounce-subtle z-20">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium">12,000+ Students</span>
                </div>
              </div>
              
              <div className="absolute bottom-6 -right-6 bg-white rounded-xl p-3 shadow-lg animate-bounce-subtle z-20" style={{animationDelay: '1s'}}>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                  <span className="text-sm font-medium">87% Job Placement</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Featured <span className="text-primary-600">Courses</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our most popular courses. Learn from industry experts with comprehensive, hands-on programs designed to get you job-ready.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.map((course, index) => (
              <CourseCard 
                key={index} 
                course={course}
                linkTo={`/course/${course.id || course.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
              />
            ))}
          </div>

          {/* View More Button */}
          <div className="text-center mt-12">
            <Link 
              to="/courses"
              className="inline-flex items-center bg-gradient-to-r from-primary-600 to-purple-600 text-white font-bold py-4 px-8 rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              View All Courses
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
            <p className="text-gray-600 text-sm mt-3">
              Discover our complete catalog of {courses.length > 0 ? courses.length : '70+'} courses
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our <span className="text-primary-600">Students Say</span>
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from real students who've transformed their careers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIconSolid key={i} className="w-5 h-5 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-gray-600 mb-6 italic leading-relaxed">
                  "{testimonial.review}"
                </p>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.course}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Career?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of successful graduates who've launched their tech careers with Aitech
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/login"
              className="bg-white text-primary-600 font-bold py-4 px-8 rounded-xl hover:bg-gray-50 transition-colors text-lg"
            >
              Access Member Area
            </Link>
            <a 
              href="tel:+2348142100460"
              className="border-2 border-white text-white font-bold py-4 px-8 rounded-xl hover:bg-white hover:text-primary-600 transition-colors text-lg"
            >
              Call Us: +234 814 210 0460
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Forum Modal */}
      {showForumModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center relative">
            <button onClick={() => setShowForumModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
            <h2 className="text-2xl font-bold text-primary-700 mb-4">Forum Coming Soon!</h2>
            <p className="text-gray-600 mb-6">Our community forum is in progress and will be available soon. Stay tuned for updates!</p>
            <div className="flex justify-center">
              <span className="bg-primary-100 text-primary-700 px-4 py-2 rounded-full font-medium">In Progress</span>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center relative">
            <button onClick={() => setShowContactModal(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
            <h2 className="text-2xl font-bold text-primary-700 mb-4">Contact Us</h2>
            <div className="mb-4">
              <p className="text-gray-700 text-lg font-medium mb-2">Aitech Technologies Nigeria</p>
              <p className="text-gray-600 mb-2">Phone: <a href="tel:+2348142100460" className="text-primary-600 hover:underline">+234 814 210 0460</a></p>
              <p className="text-gray-600 mb-2">Email: <a href="mailto:info@aitechnologiesng.com" className="text-primary-600 hover:underline">info@aitechnologiesng.com</a></p>
              <p className="text-gray-600">Address: 2nd Floor, 1, Akin Osiyemi Street, Allen Avenue, Ikeja, Lagos, Nigeria</p>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              <a href="tel:+2348142100460" className="bg-primary-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary-700 transition">Call Us</a>
              <a href="mailto:info@aitechnologiesng.com" className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition">Email Us</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage; 